import { Directive, Input, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';

import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { IconSize } from '../icon/entities/icon-sizes.entity';
import {
    ActionItem,
    ActionItemStyle,
    ApiActionBundle,
    AsyncMultiActionItem,
    BaseActionItem,
    ButtonActionItem,
    LinkActionItem,
    MultiActionItem
} from './entities/action-item.entity';

@Directive()
export class ShBaseActionItemComponent extends BaseContainer implements OnInit, OnDestroy {
    public readonly defaultIconSize: IconSize = IconSize.SMALL;
    public readonly item$: ReplaySubject<ActionItem> = new ReplaySubject<ActionItem>(1);
    public readonly childComponentClasses$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public readonly isDropdownOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readonly setDropdownOpenClass$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public classNames$!: Observable<string>;
    public labelClassNames$!: Observable<string>;
    public droppanelClassNames$!: Observable<string>;
    public pureIcon$!: Observable<boolean>;
    public title$!: Observable<string>;
    public hasVisibleAsyncItems$!: Observable<boolean>;

    @Input() bubbleEvent = true;

    @Input() set item(item: ActionItem) {
        if (typeof item !== 'undefined') {
            this.item$.next(item);
        }
    }

    @Input() set childComponentClasses(classes: string[]) {
        if (typeof classes !== 'undefined') {
            this.childComponentClasses$.next(classes);
        }
    }

    @Input() inOverflownContainer = false;

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.setupStateStreams();
        this.initHasVisibleAsyncItems();
    }

    ngOnDestroy(): void {
        this.item$.complete();
        this.childComponentClasses$.complete();
        this.isDropdownOpen$.complete();
        this.destroy$.complete();
    }

    public handleActionItemClick(event: MouseEvent, item: ButtonActionItem): void {
        if (!this.buttonActionItemGuard(item)) {
            console.error('Item is not a ButtonActionItem');
            return;
        }

        this.stopPropagationIfNeeded(event);
        item.eventListener(event);
    }

    public handleClickInsideDroppanel(event: MouseEvent): void {
        this.stopPropagationIfNeeded(event);
        this.setDropdownClosed();
    }

    public stopPropagationIfNeeded(event: MouseEvent | KeyboardEvent): void {
        if (!this.bubbleEvent) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    public setDropdownClosed(): void {
        this.isDropdownOpen$.next(false);
    }

    public toggleDropdown(): void {
        this.isDropdownOpen$.next(!this.isDropdownOpen$.value);
    }

    public linkActionItemGuard(item: ActionItem): item is LinkActionItem {
        return 'navigationLink' in item;
    }

    public buttonActionItemGuard(item: ActionItem): item is ButtonActionItem {
        return 'eventListener' in item;
    }

    public multiActionItemGuard(item: ActionItem): item is MultiActionItem {
        return 'actionItems' in item;
    }

    public asyncMultiActionItemGuard(item: ActionItem): item is AsyncMultiActionItem {
        return 'asyncActionItems' in item;
    }

    public apiActionBundleGuard(item: ActionItem): item is ApiActionBundle {
        return 'apiActions$' in item;
    }

    private setupStateStreams(): void {
        this.classNames$ = combineLatest([this.item$, this.childComponentClasses$]).pipe(
            map(([item, childComponentClasses]: [ActionItem, string[]]) =>
                this.getClassNames(item, childComponentClasses)
            ),
            shareReplay(1)
        );

        this.labelClassNames$ = of(this.getLabelClassNames()).pipe(shareReplay(1));

        this.droppanelClassNames$ = combineLatest([this.item$, this.setDropdownOpenClass$]).pipe(
            filter(
                ([item, isOpen]: [ActionItem, boolean]) =>
                    this.multiActionItemGuard(item) || this.asyncMultiActionItemGuard(item)
            ),
            map(([item, isOpen]: [ActionItem, boolean]) => this.getDroppanelClassNames(item, isOpen)),
            shareReplay(1)
        );

        this.pureIcon$ = this.item$.pipe(map(item => this.isPureIcon(item)));

        this.title$ = this.item$.pipe(map((item: ActionItem) => this.initActionItemTitle(item)));
    }

    protected getClassNames(item: ActionItem, childComponentClasses: string[]): string {
        let classNames: string[] = [];

        if (item.style) {
            classNames = [...classNames, item.style];
        } else {
            classNames = [...classNames, ActionItemStyle.Primary];
        }

        if (item.modifiers) {
            classNames = [...classNames, ...item.modifiers];
        }

        if (item.icon && !classNames.includes(ActionItemStyle.PureIconButton)) {
            classNames = [...classNames, 'sh-icon-button'];
        }

        if (item.disabled) {
            classNames = [...classNames, '-sh-disabled'];
        }

        if (childComponentClasses) {
            classNames = [...classNames, ...childComponentClasses];
        }

        return classNames.join(' ');
    }

    protected getLabelClassNames(): string {
        return '';
    }

    private getDroppanelClassNames(item: ActionItem, isOpen: boolean): string {
        let classNames: string[] = [];

        if (this.inOverflownContainer) {
            classNames.push('sh-droppanel--fixed');
        } else {
            classNames.push('sh-droppanel');
        }

        if (item.openDirection) {
            classNames = [...classNames, item.openDirection];
        }

        if (isOpen) {
            classNames = [...classNames, '-sh-is-open'];
        }

        return classNames.join(' ');
    }

    private isPureIcon(item: ActionItem): boolean {
        return !!item.style?.includes('pure');
    }

    private initActionItemTitle(item: ActionItem): string {
        if (item.disabled && !!item.disabledInfoMsg) {
            return item.label ? item.label + ':\n' + item.disabledInfoMsg : item.disabledInfoMsg;
        } else {
            return item.label;
        }
    }

    private initHasVisibleAsyncItems(): void {
        this.hasVisibleAsyncItems$ = this.item$.pipe(
            switchMap(item => {
                if (!this.asyncMultiActionItemGuard(item)) {
                    return of(false);
                }

                // Combine all async action items and check if any are visible
                if (item.asyncActionItems.length === 0) {
                    return of(false);
                }

                return combineLatest(
                    item.asyncActionItems.map(multiItem$ =>
                        multiItem$.pipe(
                            switchMap(asyncItem => {
                                if (asyncItem === null) {
                                    return of(false);
                                }

                                // Check if item is ApiActionBundle with apiActions$
                                if (this.apiActionBundleGuard(asyncItem)) {
                                    return (asyncItem as ApiActionBundle).apiActions$.pipe(
                                        map(apiActions => apiActions.length > 0 && apiActions.some(a => !a.hidden))
                                    );
                                }

                                // Regular action item - check if not hidden
                                return of(!(asyncItem as BaseActionItem).hidden);
                            })
                        )
                    )
                ).pipe(map(results => results.some(result => result)));
            }),
            takeUntil(this.destroy$)
        );
    }

    public linkPressed(event: KeyboardEvent): void {
        event.preventDefault();
        (event.target as HTMLElement).click();
    }
}
